import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ReferenceDot,
  ResponsiveContainer,
} from 'recharts';
import { History, Loader2, RefreshCw } from 'lucide-react';

import fuelApi from '@/services/fuelApi';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import DateTimePicker24h from '@/components/ui/datetime-picker';
import { useTimezone } from '@/contexts/TimezoneContext';
import { showErrorToast } from '@/utils/errorHandler';

// "Histórico do tanque" (D-15/D-16/D-17, Plan 05): a série saldo(t) é computada
// NO SERVIDOR (GET /api/fuel/timeline — âncora − consumo cumulativo gap-aware,
// mesma regra de CC-2026-07-07-005/TELEMETRY-GAP-RULE.md). Este componente SÓ
// PLOTA os arrays já prontos da resposta (INV-UI-TRAIL-001) — nenhuma soma,
// integral ou derivação de saldo/consumo acontece aqui.

const WINDOW_OPTIONS = [
  { id: '24h', label: '24h', hours: 24 },
  { id: '7d', label: '7 dias', hours: 24 * 7 },
  { id: '30d', label: '30 dias', hours: 24 * 30 },
  { id: 'custom', label: 'Personalizado', hours: null },
];

const fmtL = (l) =>
  l == null || Number.isNaN(l) ? '—' : `${Math.round(l).toLocaleString('pt-BR')} L`;

const pad2 = (n) => String(n).padStart(2, '0');
const toLocalInputValue = (date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`;

const EVENT_COLOR = { refuel: '#22c55e', saldo_override: '#a855f7' };
const EVENT_LABEL = { refuel: 'Abastecimento', saldo_override: 'Ajuste' };

// Uma cor por tanque (principal primeiro — mantém o amarelo histórico);
// tons distintos entre si e do saldo/reserva (azul/vermelho) no tema escuro.
const SENSOR_COLORS = ['#eab308', '#f97316', '#14b8a6', '#ec4899', '#8b5cf6', '#84cc16', '#06b6d4', '#f43f5e'];

export default function FuelTimeline({ equipmentId }) {
  const { formatDateTime } = useTimezone();

  const [windowId, setWindowId] = useState('24h');
  const [customStart, setCustomStart] = useState(() =>
    toLocalInputValue(new Date(Date.now() - 24 * 3600 * 1000)),
  );
  const [customEnd, setCustomEnd] = useState(() => toLocalInputValue(new Date()));
  const [customRange, setCustomRange] = useState(null);
  const [reloadNonce, setReloadNonce] = useState(0);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Janelas fixas: recalcula "agora" quando a janela muda ou o usuário clica
  // em Atualizar. reloadNonce força refetch sem trocar de janela.
  const presetRange = useMemo(() => {
    const opt = WINDOW_OPTIONS.find((w) => w.id === windowId);
    if (!opt || !opt.hours) return null;
    const stop = new Date();
    const start = new Date(stop.getTime() - opt.hours * 3600 * 1000);
    return { start: start.toISOString(), stop: stop.toISOString() };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowId, reloadNonce]);

  const activeRange = windowId === 'custom' ? customRange : presetRange;

  const applyCustomRange = useCallback(() => {
    const start = new Date(customStart);
    const stop = new Date(customEnd);
    if (Number.isNaN(start.getTime()) || Number.isNaN(stop.getTime()) || stop <= start) {
      toast.error('Selecione um período custom válido (fim após o início).');
      return;
    }
    setCustomRange({ start: start.toISOString(), stop: stop.toISOString() });
  }, [customStart, customEnd]);

  useEffect(() => {
    if (!equipmentId || !activeRange) {
      setData(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    fuelApi
      .getTimeline(equipmentId, activeRange)
      .then((res) => {
        if (!cancelled) setData(res.data || null);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[FuelTimeline] erro ao carregar histórico do tanque:', err);
          showErrorToast(toast, err, 'Erro ao carregar o histórico do tanque.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [equipmentId, activeRange]);

  // Sensores de TODOS os tanques no mesmo timeline (pedido do dono, D-14):
  // uma série por tanque — a transferência principal→filtrado aparece como
  // espelho (um desce, o outro sobe). `sensors` vem pronto do servidor;
  // fallback para o `sensor` legado (série única) durante a janela de deploy.
  const sensorSeries = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data.sensors) && data.sensors.length > 0) return data.sensors;
    if ((data.sensor || []).length > 0) {
      return [{ tank_id: 'main', label: 'Sensor', points: data.sensor }];
    }
    return [];
  }, [data]);

  // Merge saldo + sensores por timestamp — só reorganiza os pontos que já
  // vieram prontos do servidor num único array pra alimentar o ComposedChart;
  // nenhum valor é somado/derivado aqui.
  const chartData = useMemo(() => {
    if (!data) return [];
    const map = new Map();
    (data.points || []).forEach((p) => {
      const tsMs = new Date(p.ts).getTime();
      const row = map.get(tsMs) || { tsMs, ts: p.ts };
      row.saldo_l = p.saldo_l;
      map.set(tsMs, row);
    });
    sensorSeries.forEach((serie, i) => {
      (serie.points || []).forEach((s) => {
        const tsMs = new Date(s.ts).getTime();
        const row = map.get(tsMs) || { tsMs, ts: s.ts };
        row[`level_${i}`] = s.level_l;
        map.set(tsMs, row);
      });
    });
    return Array.from(map.values()).sort((a, b) => a.tsMs - b.tsMs);
  }, [data, sensorSeries]);

  const eventMarkers = useMemo(
    () => (data?.events || []).map((e) => ({ ...e, tsMs: new Date(e.ts).getTime() })),
    [data],
  );

  const hasSensor = Boolean(data?.has_sensor);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <div className="bg-background border rounded-lg p-2 shadow-lg">
        <p className="text-xs text-muted-foreground mb-1">
          {formatDateTime(new Date(label), { omitSeconds: true })}
        </p>
        {payload.map((entry, idx) => (
          <p key={idx} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {fmtL(entry.value)}
          </p>
        ))}
      </div>
    );
  };

  return (
    <Card data-testid="fuel-timeline-section">
      <CardHeader className="pb-2">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <CardTitle className="text-base flex items-center gap-2">
            <History className="w-4 h-4" />
            Histórico do tanque
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            {WINDOW_OPTIONS.map((opt) => (
              <Button
                key={opt.id}
                type="button"
                size="sm"
                variant={windowId === opt.id ? 'default' : 'outline'}
                onClick={() => setWindowId(opt.id)}
                data-testid={`fuel-timeline-window-${opt.id}`}
              >
                {opt.label}
              </Button>
            ))}
            <Button
              type="button"
              size="icon"
              variant="outline"
              onClick={() => setReloadNonce((n) => n + 1)}
              disabled={loading || !equipmentId}
              aria-label="Atualizar histórico do tanque"
              data-testid="fuel-timeline-refresh"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        {windowId === 'custom' && (
          <div className="flex flex-wrap items-end gap-2 pt-2">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Início</p>
              <DateTimePicker24h
                value={customStart}
                onChange={setCustomStart}
                ariaLabel="Início do período do histórico do tanque"
              />
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Fim</p>
              <DateTimePicker24h
                value={customEnd}
                onChange={setCustomEnd}
                ariaLabel="Fim do período do histórico do tanque"
              />
            </div>
            <Button
              type="button"
              size="sm"
              onClick={applyCustomRange}
              data-testid="fuel-timeline-custom-apply"
            >
              Aplicar
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {/* D-07: equipamento sem sensor NUNCA oculta a seção — mostra o saldo
            computado (lançamentos + consumo) com um aviso explícito. */}
        {!hasSensor && (
          <p className="text-xs text-muted-foreground mb-2">
            Sem sensor de nível configurado para este equipamento — exibindo só o saldo
            computado (lançamentos + consumo). Configure o sensor na aba Tanques para
            sobrepor a leitura.
          </p>
        )}
        <div data-testid="fuel-timeline-chart">
          {chartData.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              {loading ? 'Carregando histórico do tanque…' : 'Sem dados de saldo no período selecionado.'}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis
                  dataKey="tsMs"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  stroke="#888"
                  tickFormatter={(v) => formatDateTime(new Date(v), { omitSeconds: true })}
                />
                <YAxis stroke="#888" tickFormatter={(v) => fmtL(v)} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                {data?.reserve_threshold_l != null && (
                  <ReferenceLine
                    y={data.reserve_threshold_l}
                    stroke="#ef4444"
                    strokeDasharray="4 4"
                    label={{ value: 'Reserva mínima', position: 'insideTopRight', fill: '#ef4444', fontSize: 11 }}
                  />
                )}
                <Line
                  type="stepAfter"
                  dataKey="saldo_l"
                  name="Saldo"
                  stroke="#3b82f6"
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
                {sensorSeries.map((serie, i) => (
                  <Line
                    key={serie.tank_id || i}
                    type="monotone"
                    dataKey={`level_${i}`}
                    name={sensorSeries.length > 1 ? `Sensor — ${serie.label}` : 'Sensor'}
                    stroke={SENSOR_COLORS[i % SENSOR_COLORS.length]}
                    strokeDasharray="4 3"
                    dot={false}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                ))}
                {eventMarkers.map((ev, idx) => (
                  <ReferenceDot
                    key={ev.ts || idx}
                    x={ev.tsMs}
                    y={ev.saldo_after_l}
                    r={5}
                    fill={EVENT_COLOR[ev.type] || '#94a3b8'}
                    stroke="none"
                    label={{
                      value: ev.type === 'refuel' ? `+${fmtL(ev.volume_l)}` : (EVENT_LABEL[ev.type] || ev.type),
                      position: 'top',
                      fontSize: 10,
                      fill: EVENT_COLOR[ev.type] || '#94a3b8',
                    }}
                  />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
